import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Menu,
  MenuButton,
  MenuHeading,
  MenuItem,
  MenuItems,
  MenuSection,
  MenuSeparator
} from '@headlessui/react';
import React from 'react';
import { type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { cn } from '../lib/utils';

// ── Variants ──────────────────────────────────────────────────────────────────

const dropdownMenuVariants = tv({
  slots: {
    root: 'relative inline-block',
    content: [
      'z-50 min-w-40 overflow-hidden rounded-lg border py-1',
      'border-uniform-gray-200 bg-white shadow-lg',
      'dark:border-uniform-gray-700 dark:bg-uniform-gray-900',
      // HUI v2 anchor transition classes
      'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0'
    ],
    item: [
      'flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-sm text-left',
      'text-uniform-gray-700 dark:text-uniform-gray-300',
      'data-[focus]:bg-uniform-gray-100 data-[focus]:text-uniform-gray-900',
      'dark:data-[focus]:bg-uniform-gray-800 dark:data-[focus]:text-uniform-gray-100',
      'disabled:pointer-events-none disabled:opacity-50',
      '[outline:none]'
    ],
    itemIcon: 'h-4 w-4 shrink-0 text-uniform-gray-500 dark:text-uniform-gray-400',
    separator: 'my-1 border-t border-uniform-gray-100 dark:border-uniform-gray-800',
    groupLabel: [
      'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide',
      'text-uniform-gray-600 dark:text-uniform-gray-400'
    ]
  },
  variants: {
    itemColor: {
      default: {},
      destructive: {
        item: 'text-uniform-red-700 dark:text-uniform-red-400 data-[focus]:bg-uniform-red-50 data-[focus]:text-uniform-red-700 dark:data-[focus]:bg-uniform-red-950 dark:data-[focus]:text-uniform-red-300',
        itemIcon: 'text-uniform-red-600 dark:text-uniform-red-500'
      },
      warning: {
        item: 'text-yellow-700 dark:text-yellow-400 data-[focus]:bg-yellow-50 data-[focus]:text-yellow-800 dark:data-[focus]:bg-yellow-950 dark:data-[focus]:text-yellow-300',
        itemIcon: 'text-yellow-700 dark:text-yellow-400'
      }
    }
  },
  defaultVariants: { itemColor: 'default' }
});

type DropdownMenuVariants = VariantProps<typeof dropdownMenuVariants>;

// ── DropdownMenu (root) ───────────────────────────────────────────────────────

interface DropdownMenuProps {
  children?: ReactNode;
  className?: string;
}

function DropdownMenu({ children, className }: DropdownMenuProps) {
  const { root } = dropdownMenuVariants();
  return (
    <Menu as="div" className={cn(root(), className)}>
      {children}
    </Menu>
  );
}

// ── DropdownMenuTrigger ───────────────────────────────────────────────────────

interface DropdownMenuTriggerProps {
  children?: ReactNode;
  className?: string;
  'aria-label'?: string;
}

function DropdownMenuTrigger({
  children,
  className,
  'aria-label': ariaLabel
}: DropdownMenuTriggerProps) {
  return (
    <MenuButton
      as="div"
      aria-label={ariaLabel}
      className={cn('inline-flex cursor-pointer [outline:none]', className)}>
      {children}
    </MenuButton>
  );
}

// ── DropdownMenuContent ───────────────────────────────────────────────────────

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

interface DropdownMenuContentProps {
  children?: ReactNode;
  /** Which side of the trigger to open toward. Defaults to "bottom" */
  side?: Side;
  /** Alignment along the trigger's axis. Defaults to "start" */
  align?: Align;
  className?: string;
}

function DropdownMenuContent({
  children,
  side = 'bottom',
  align = 'start',
  className
}: DropdownMenuContentProps) {
  const { content } = dropdownMenuVariants();
  const anchor = `${side} ${align}` as Parameters<typeof MenuItems>[0]['anchor'];
  return (
    <MenuItems anchor={anchor} className={cn(content(), '[--anchor-gap:4px]', className)}>
      {children}
    </MenuItems>
  );
}

// ── DropdownMenuItem ──────────────────────────────────────────────────────────

interface DropdownMenuItemProps {
  children?: ReactNode;
  icon?: IconDefinition;
  /** Semantic color for destructive or warning actions */
  color?: 'default' | 'destructive' | 'warning';
  disabled?: boolean;
  onClick?: () => void;
  /** Renders the item as a link. Defaults the element to "a" */
  href?: string;
  rel?: string;
  target?: '_blank';
  /** Override the rendered element. Use "div" when children contain their own interactive element. */
  as?: React.ElementType;
  className?: string;
}

function DropdownMenuItem({
  children,
  icon,
  color = 'default',
  disabled,
  onClick,
  href,
  rel,
  target,
  as,
  className
}: DropdownMenuItemProps) {
  const Tag = as ?? (href ? 'a' : 'button');
  const { item, itemIcon } = dropdownMenuVariants({ itemColor: color });
  return (
    <MenuItem disabled={disabled}>
      <Tag
        type={Tag === 'button' ? 'button' : undefined}
        href={href}
        rel={rel}
        target={target}
        onClick={onClick}
        className={cn(item(), className)}>
        {icon && <FontAwesomeIcon icon={icon} className={itemIcon()} />}
        {children}
      </Tag>
    </MenuItem>
  );
}

// ── DropdownMenuSeparator ─────────────────────────────────────────────────────

interface DropdownMenuSeparatorProps {
  className?: string;
}

function DropdownMenuSeparator({ className }: DropdownMenuSeparatorProps) {
  const { separator } = dropdownMenuVariants();
  return <MenuSeparator className={cn(separator(), className)} />;
}

// ── DropdownMenuGroup ─────────────────────────────────────────────────────────

interface DropdownMenuGroupProps {
  label?: string;
  children?: ReactNode;
  className?: string;
}

function DropdownMenuGroup({ label, children, className }: DropdownMenuGroupProps) {
  const { groupLabel } = dropdownMenuVariants();
  return (
    <MenuSection className={className}>
      {label && <MenuHeading className={groupLabel()}>{label}</MenuHeading>}
      {children}
    </MenuSection>
  );
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  dropdownMenuVariants
};
export type {
  DropdownMenuProps,
  DropdownMenuTriggerProps,
  DropdownMenuContentProps,
  DropdownMenuItemProps,
  DropdownMenuSeparatorProps,
  DropdownMenuGroupProps,
  DropdownMenuVariants,
  Side,
  Align
};
